import React from 'react';
import { X, ExternalLink, Building, Calendar, Clock, DollarSign } from 'lucide-react';

interface CitaItem {
    citaId: number;
    codigo: string;
    fecha: string;
    hora: string;
    servicio: string;
    estado: string;
    monto: number;
}

interface SucursalCitasModalProps {
    isOpen: boolean;
    onClose: () => void;
    sucursal: {
        nombre: string;
        citas: CitaItem[];
    };
}

const SucursalCitasModal: React.FC<SucursalCitasModalProps> = ({ isOpen, onClose, sucursal }) => {
    if (!isOpen) return null;

    const getEstadoBadge = (estado: string) => {
        switch (estado.toLowerCase()) {
            case 'confirmada':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#8b3fa8]/10 to-[#61257b]/10 text-[#61257b] border border-[#8b3fa8]/20">
                        Confirmada
                    </span>
                );
            case 'pendiente':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#ffd97c]/10 to-[#ea8406]/10 text-[#ea8406] border border-[#ffd97c]/20">
                        Pendiente
                    </span>
                );
            case 'cancelada':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10 text-[#8f1f64] border border-[#c1348b]/20">
                        Cancelada
                    </span>
                );
            default:
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {estado}
                    </span>
                );
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
                    onClick={onClose}
                    aria-hidden="true"
                ></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div className="bg-gradient-to-r from-[#61257b] to-[#8b3fa8] px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Building className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">
                                        Citas en {sucursal.nombre}
                                    </h3>
                                    <p className="text-sm text-white/80">
                                        {sucursal.citas.length} citas registradas
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-lg"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto">
                        {sucursal.citas.length > 0 ? (
                            <div className="space-y-4">
                                {sucursal.citas.map((cita) => (
                                    <div
                                        key={cita.citaId}
                                        className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all"
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-start justify-between mb-3">
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className="text-sm font-mono font-semibold text-gray-800 bg-gray-100 px-3 py-1.5 rounded-lg">
                                                                {cita.codigo}
                                                            </span>
                                                            {getEstadoBadge(cita.estado)}
                                                        </div>
                                                        <h4 className="text-base font-medium text-gray-800">
                                                            {cita.servicio}
                                                        </h4>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Calendar className="h-4 w-4 text-gray-500" />
                                                            <p className="text-xs text-gray-500">Fecha</p>
                                                        </div>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {cita.fecha}
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Clock className="h-4 w-4 text-gray-500" />
                                                            <p className="text-xs text-gray-500">Hora</p>
                                                        </div>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            {cita.hora}
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <DollarSign className="h-4 w-4 text-gray-500" />
                                                            <p className="text-xs text-gray-500">Monto</p>
                                                        </div>
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            Bs {cita.monto.toFixed(2)}
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-center">
                                                        <button
                                                            onClick={() => {
                                                                const usuarioId = window.location.pathname.split('/').pop();
                                                                window.open(`/dashboard/kardex-clientes/sesiones/${cita.citaId}?usuarioId=${usuarioId}`, '_blank');
                                                            }}
                                                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#c1348b] to-[#8f1f64] hover:shadow-md rounded-lg transition-all"
                                                        >
                                                            <ExternalLink className="mr-2 h-4 w-4" />
                                                            Ver Detalles
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                    <Building className="h-8 w-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                    No hay citas registradas
                                </h3>
                                <p className="text-gray-600 text-sm max-w-md mx-auto">
                                    No se encontraron citas registradas para esta sucursal.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2.5 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium transition-colors"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SucursalCitasModal;